import React, { useEffect, useState, useRef } from "react";
import { useLocation, Link } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, Download } from "lucide-react";
import { Button } from "../components/ui/button";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CheckoutSuccess() {
  const location = useLocation();
  const { clearCart } = useCart();
  const { isAuthenticated } = useAuth();
  const [status, setStatus] = useState("checking"); // checking | paid | pending | error
  const cleared = useRef(false);
  const isLoggedIn = isAuthenticated;

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setStatus("error");
      return;
    }

    let attempts = 0;
    const maxAttempts = 6;
    let timer;

    const poll = async () => {
      try {
        const res = await axios.get(`${API}/payments/checkout-status/${sessionId}`);
        const data = res.data || {};
        if (data.payment_status === "paid") {
          if (!cleared.current) {
            cleared.current = true;
            clearCart();
          }
          setStatus("paid");
          return;
        }
        if (data.status === "expired") {
          setStatus("error");
          return;
        }
        attempts += 1;
        if (attempts >= maxAttempts) {
          setStatus("pending");
          return;
        }
        timer = setTimeout(poll, 2000);
      } catch {
        attempts += 1;
        if (attempts >= maxAttempts) {
          setStatus("error");
          return;
        }
        timer = setTimeout(poll, 2000);
      }
    };

    poll();
    return () => clearTimeout(timer);
  }, [location.search, clearCart]);

  return (
    <div className="min-h-screen bg-parchment flex items-center justify-center px-4 py-20" data-testid="checkout-success-page">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg border border-slate-200 p-8 text-center">
        {status === "checking" && (
          <>
            <Loader2 className="w-12 h-12 text-kingdom-gold mx-auto mb-4 animate-spin" />
            <h1 className="font-heading text-2xl text-royal-blue mb-2">Confirming your payment…</h1>
            <p className="text-slate-500 text-sm">Please wait while we verify your transaction.</p>
          </>
        )}

        {status === "paid" && (
          <>
            <CheckCircle2 className="w-14 h-14 text-green-500 mx-auto mb-4" data-testid="success-icon" />
            <h1 className="font-heading text-2xl text-royal-blue mb-2">Payment Successful</h1>
            <p className="text-slate-600 text-sm mb-6">
              {"Thank you for your support of The Kingdom of Yunah'Shach'Qal Yashar'al. Your purchases and access have been unlocked."}
            </p>
            <div className="space-y-2">
              {isLoggedIn ? (
                <Link to="/portal">
                  <Button className="w-full btn-gold" data-testid="success-portal-btn">
                    <Download className="w-4 h-4 mr-2" /> Go to My Portal
                  </Button>
                </Link>
              ) : (
                <Link to="/subscribers/signup">
                  <Button className="w-full btn-gold" data-testid="success-claim-access-btn">
                    <Download className="w-4 h-4 mr-2" /> Claim Your Purchases
                  </Button>
                </Link>
              )}
              <Link to="/shop">
                <Button variant="outline" className="w-full" data-testid="success-shop-btn">
                  Continue Shopping
                </Button>
              </Link>
            </div>
            {!isLoggedIn && (
              <p className="text-xs text-slate-400 mt-4">
                Create an account with the <strong>same email you used at checkout</strong> to unlock your
                downloads and subscription.{" "}
                <Link to="/subscribers/login" className="text-royal-blue underline">Already have an account? Log in</Link>
              </p>
            )}
          </>
        )}

        {status === "pending" && (
          <>
            <Loader2 className="w-12 h-12 text-kingdom-gold mx-auto mb-4" />
            <h1 className="font-heading text-2xl text-royal-blue mb-2">Payment Processing</h1>
            <p className="text-slate-600 text-sm mb-6">
              Your payment is still being processed. Access will be unlocked automatically once it
              completes — check your portal shortly.
            </p>
            <Link to={isLoggedIn ? "/portal" : "/subscribers/login"}>
              <Button className="w-full btn-gold">{isLoggedIn ? "Go to My Portal" : "Access Subscriber Portal"}</Button>
            </Link>
          </>
        )}

        {status === "error" && (
          <>
            <XCircle className="w-14 h-14 text-red-500 mx-auto mb-4" />
            <h1 className="font-heading text-2xl text-royal-blue mb-2">Something went wrong</h1>
            <p className="text-slate-600 text-sm mb-6">
              {"We couldn't confirm your payment. If you were charged, your access will still be unlocked automatically. Otherwise, please try again."}
            </p>
            <div className="space-y-2">
              {!isLoggedIn && (
                <Link to="/subscribers/signup">
                  <Button className="w-full btn-gold" data-testid="error-claim-access-btn">
                    <Download className="w-4 h-4 mr-2" /> Claim Your Purchases
                  </Button>
                </Link>
              )}
              <Link to="/cart">
                <Button variant={isLoggedIn ? "default" : "outline"} className={isLoggedIn ? "w-full btn-gold" : "w-full"}>
                  Return to Cart
                </Button>
              </Link>
            </div>
            {!isLoggedIn && (
              <p className="text-xs text-slate-400 mt-4">
                If you already paid, create an account with the <strong>same email you used at checkout</strong> to unlock your access.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
